import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import groupBy from "lodash/groupBy";
import { useQuery } from "@tanstack/react-query";
import { SearchDecorators } from "@graylog/server-api";
import { Button } from "components/bootstrap";
import { IfPermitted } from "components/common";
import Spinner from "components/common/Spinner";
import { fetchStreams } from "api/streams";
import UserNotification from "util/UserNotification";
import DecoratorList from "views/components/messagelist/decorators/DecoratorList";
import { defaultCompare } from "logic/DefaultCompare";
import DecoratorsConfigUpdate from "./decorators/DecoratorsConfigUpdate";
import { DEFAULT_SEARCH_ID } from "./decorators/StreamSelect";
import DecoratorsUpdater from "./decorators/DecoratorsUpdater";
import formatDecorator from "./decorators/FormatDecorator";
const DecoratorsConfig = () => {
  const { t } = useTranslation("configurations");
  const { data: streams, isLoading: streamsLoading } = useQuery({
    queryKey: ["streamsMap"],
    queryFn: () => fetchStreams()
  });
  const { data: types, isLoading: typesLoading } = useQuery({
    queryKey: ["decorators", "types"],
    queryFn: () => SearchDecorators.getAvailable()
  });
  const {
    data: decorators,
    isLoading: decoratorsLoading,
    refetch: refetchDecorators
  } = useQuery({
    queryKey: ["decorators", "list"],
    queryFn: () => SearchDecorators.get()
  });
  const [showConfigModal, setShowConfigModal] = useState(false);
  const streamsMap = useMemo(() => {
    var _a;
    return Object.fromEntries((_a = streams == null ? void 0 : streams.map((s) => [s.id, s])) != null ? _a : []);
  }, [streams]);
  const openModal = useCallback(() => setShowConfigModal(true), []);
  const closeModal = useCallback(() => setShowConfigModal(false), []);
  const onSave = useCallback(
    (newDecorators) => DecoratorsUpdater(newDecorators, decorators).then(
      () => UserNotification.success(t("decorators.save_success"), t("decorators.save_success_title")),
      (error) => UserNotification.error(t("decorators.save_error", { error }), t("decorators.save_error_title"))
    ).then(() => refetchDecorators()).then(closeModal),
    [closeModal, decorators, refetchDecorators, t]
  );
  const decoratorMap = useMemo(() => {
    if (typesLoading || decoratorsLoading) {
      return /* @__PURE__ */ React.createElement(Spinner, null);
    }
    if (!decorators || decorators.length === 0) {
      return /* @__PURE__ */ React.createElement("i", null, t("decorators.none_configured"));
    }
    const decoratorsGroupedByStream = groupBy(decorators, (decorator) => decorator.stream || DEFAULT_SEARCH_ID);
    return Object.entries(decoratorsGroupedByStream).map(
      ([id, _decorators]) => {
        var _a, _b;
        return [
          (_b = (_a = streamsMap[id]) == null ? void 0 : _a.title) != null ? _b : id,
          _decorators.sort((d1, d2) => d1.order - d2.order).map((decorator) => formatDecorator(decorator, _decorators, types))
        ];
      }
    ).sort((entry1, entry2) => defaultCompare(entry1[0], entry2[0])).map(([streamName, _decorators]) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("dt", null, streamName), /* @__PURE__ */ React.createElement("dd", null, /* @__PURE__ */ React.createElement(DecoratorList, { decorators: _decorators, disableDragging: true }))));
  }, [decorators, decoratorsLoading, streamsMap, t, types, typesLoading]);
  if (streamsLoading || typesLoading || decoratorsLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, t("decorators.title")), /* @__PURE__ */ React.createElement("p", null, t("decorators.description")), /* @__PURE__ */ React.createElement("p", null, decoratorMap), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "decorators:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, t("decorators.edit"))), /* @__PURE__ */ React.createElement(
    DecoratorsConfigUpdate,
    {
      show: showConfigModal,
      streams,
      decorators,
      onCancel: closeModal,
      onSave,
      types
    }
  ));
};
export default DecoratorsConfig;
